<script setup>
import { computed, toRef } from 'vue';
import { useDialogA11y } from 'dashboard/composables/useDialogA11y';

const props = defineProps({
  show: { type: Boolean, default: false },
  title: { type: String, default: 'Confirmar ação' },
  description: { type: String, default: '' },
  confirmText: { type: String, default: 'Confirmar' },
  cancelText: { type: String, default: 'Cancelar' },
  confirmVariant: { type: String, default: 'danger' },
  isLoading: { type: Boolean, default: false },
  confirmDisabled: { type: Boolean, default: false },
  maxWidthClass: { type: String, default: 'max-w-[420px]' },
  icon: { type: String, default: '' },
  iconClass: { type: String, default: 'bg-lais-primary-50 text-lais-primary' },
});

const emit = defineEmits(['close', 'confirm']);

const overlayStyle = { backgroundColor: 'rgba(15, 23, 42, 0.42)' };
const confirmButtonClass = computed(() =>
  props.confirmVariant === 'primary'
    ? 'bg-lais-primary text-white hover:bg-lais-primary-600'
    : 'bg-lais-danger text-white hover:bg-red-600'
);

const titleId = `lais-confirm-${Math.random().toString(36).slice(2)}`;
const descriptionId = `lais-confirm-description-${Math.random()
  .toString(36)
  .slice(2)}`;
const { dialogRef } = useDialogA11y({
  isOpen: toRef(props, 'show'),
  onClose: () => emit('close'),
});
</script>

<template>
  <div>
    <div
      v-if="show"
      class="fixed inset-0 z-lais-modal grid place-items-center"
      :style="overlayStyle"
      @click.self="emit('close')"
    >
      <div
        ref="dialogRef"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="description ? descriptionId : undefined"
        tabindex="-1"
        class="mx-4 w-full rounded-lais bg-white shadow-lais-pop"
        :class="maxWidthClass"
      >
        <header class="border-b border-lais-border-rule px-6 py-4">
          <slot name="icon">
            <span
              v-if="icon"
              class="mb-3 grid size-11 place-items-center rounded-full"
              :class="iconClass"
              aria-hidden="true"
            >
              <span class="size-5" :class="icon" />
            </span>
          </slot>
          <h2
            :id="titleId"
            class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
          >
            {{ title }}
          </h2>
        </header>
        <div class="px-6 py-4">
          <p
            v-if="description"
            :id="descriptionId"
            class="text-lais-body-sm text-lais-text-body"
          >
            {{ description }}
          </p>
          <slot />
        </div>
        <footer
          class="flex items-center justify-end gap-3 border-t border-lais-border-rule px-6 py-4"
        >
          <button
            type="button"
            class="h-10 rounded-lais px-4 text-lais-body-sm font-semibold text-lais-text-body hover:text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
            @click="emit('close')"
          >
            {{ cancelText }}
          </button>
          <button
            type="button"
            class="inline-flex h-10 items-center gap-2 rounded-lais px-4 text-lais-body-sm font-semibold transition-colors focus-visible:outline-none focus-visible:shadow-lais-focus disabled:cursor-not-allowed disabled:opacity-60"
            :class="confirmButtonClass"
            :disabled="isLoading || confirmDisabled"
            @click="emit('confirm')"
          >
            <span
              v-if="isLoading"
              class="i-lucide-loader-circle size-4 animate-spin"
              aria-hidden="true"
            />
            {{ confirmText }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>
